/**
 * RenameAgentModal Component
 * 
 * A modal dialog for renaming an agent with:
 * - Edit icon centered at top
 * - Title and description centered
 * - Input for new name
 * - Cancel and Rename buttons
 * - Glass morphism styling
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import LoadingSpinner from '../LoadingSpinner';

interface RenameAgentModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Current agent name */
  currentName: string;
  /** Callback when rename is confirmed */
  onRename: (newName: string) => Promise<void>;
}

const RenameAgentModal = ({ open, onClose, currentName, onRename }: RenameAgentModalProps) => {
  const [newName, setNewName] = useState(currentName);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setNewName(currentName);
      setError(null);
      setIsLoading(false);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 100);
    }
  }, [open, currentName]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  // Handle rename
  const handleRename = async () => {
    const trimmedName = newName.trim();
    
    if (!trimmedName) {
      setError('Name cannot be empty');
      return;
    }

    if (trimmedName === currentName) {
      onClose();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await onRename(trimmedName);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename agent');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      e.preventDefault();
      handleRename();
    } else if (e.key === 'Escape' && !isLoading) {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] animate-slide-in-center w-[calc(100vw-32px)] lg:w-auto"
      >
        <div className="w-full lg:w-[420px] bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
          {/* Close button */}
          {!isLoading && (
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors z-10"
              aria-label="Close"
            >
              <CloseIcon width="14" height="14" />
            </button>
          )}

          {/* Content */}
          <div className="px-8 py-8">
            {/* Edit Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#208AEC]/10 flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-[#208AEC]">
                  <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-white text-center mb-2">
              Rename Agent
            </h2>

            {/* Description */}
            <p className="text-sm text-white/60 text-center mb-6">
              Enter a new name for your agent below.
            </p>

            {/* Input */}
            <div className="mb-4">
              <input
                ref={inputRef}
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Agent name"
                disabled={isLoading}
                maxLength={128}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center placeholder-white/30 focus:outline-none focus:border-[#208AEC]/50 transition-colors disabled:opacity-50"
              />
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-400 text-center mb-4">{error}</p>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-2.5 text-sm font-medium text-white/70 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors disabled:opacity-50"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={handleRename}
                disabled={isLoading || !newName.trim()}
                className="px-6 py-2.5 text-sm font-medium text-white bg-[#208AEC] hover:bg-[#1a7ad4] rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading && <LoadingSpinner width="14px" height="14px" />}
                <span>Rename</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default RenameAgentModal;
